import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { Clock, ArrowLeft } from "lucide-react";
import PageShell from "@/components/PageShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Render plain-text body with markdown-lite support:
 * - blank line → paragraph
 * - "## " prefix → h3 heading
 * - **bold** inline → <strong>
 */
const renderBody = (body) => {
  if (!body) return null;
  const blocks = body.split(/\n\n+/);
  return blocks.map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h3
          key={i}
          className="font-display text-2xl lg:text-3xl font-light mt-12 mb-5 text-[#E6D5B8]"
        >
          {block.replace(/^##\s+/, "")}
        </h3>
      );
    }
    // bold inline
    const parts = block.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p
        key={i}
        className="text-base lg:text-lg leading-relaxed text-[#F9F6F0]/80 font-light my-5"
      >
        {parts.map((part, j) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return (
              <strong key={j} className="text-[#F9F6F0] font-semibold">
                {part.slice(2, -2)}
              </strong>
            );
          }
          return <span key={j}>{part}</span>;
        })}
      </p>
    );
  });
};

const InsightDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | notfound | error

  useEffect(() => {
    let mounted = true;
    setStatus("loading");
    axios
      .get(`${API}/posts/${slug}`)
      .then((res) => {
        if (!mounted) return;
        setPost(res.data);
        setStatus("ready");
      })
      .catch((e) => {
        if (!mounted) return;
        if (e?.response?.status === 404) setStatus("notfound");
        else setStatus("error");
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  return (
    <PageShell testid="insight-detail-page">
      <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 pb-16">
        <Link
          to="/insights"
          data-testid="insight-back-link"
          className="inline-flex items-center gap-2 text-xs tracking-luxury uppercase text-[#F9F6F0]/55 hover:text-[#E6D5B8] transition-colors mb-10"
        >
          <ArrowLeft size={14} />
          <span>Todos los insights</span>
        </Link>

        {status === "loading" && (
          <div className="space-y-4 animate-pulse">
            <div className="h-4 bg-white/10 rounded w-1/4" />
            <div className="h-12 bg-white/10 rounded w-3/4" />
            <div className="h-4 bg-white/10 rounded w-2/4" />
          </div>
        )}

        {status === "notfound" && (
          <div data-testid="insight-notfound">
            <h1 className="font-display text-3xl lg:text-4xl font-light">
              Este insight ya no está disponible.
            </h1>
            <p className="mt-4 text-[#F9F6F0]/65">
              Es posible que haya sido archivado o que el enlace contenga un
              error.
            </p>
            <Link
              to="/insights"
              className="inline-block mt-7 text-sm tracking-luxury uppercase text-[#E6D5B8] underline underline-offset-4"
            >
              Ver insights actuales
            </Link>
          </div>
        )}

        {status === "error" && (
          <div className="text-[#F9F6F0]/65">
            Ocurrió un error cargando el insight. Vuelve a intentar más tarde.
          </div>
        )}

        {status === "ready" && post && (
          <>
            <header className="mb-12">
              <div className="flex items-center gap-3 mb-5 flex-wrap">
                <span className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] bg-[#3D1565]/30 border border-[#3D1565] px-3 py-1.5 rounded-sm">
                  {post.category}
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-[#F9F6F0]/55">
                  <Clock size={11} />
                  {post.reading_minutes} min de lectura
                </span>
                <span className="text-[11px] text-[#F9F6F0]/55">
                  · {post.published_at}
                </span>
              </div>
              <h1
                data-testid="insight-title"
                className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-light leading-[1.08]"
              >
                {post.title}
              </h1>
              <p className="mt-6 text-lg text-[#F9F6F0]/70 font-light leading-relaxed">
                {post.excerpt}
              </p>
              <div className="mt-7 pt-5 border-t border-white/10 text-[11px] tracking-luxury uppercase text-[#A397B4]">
                Por {post.author}
              </div>
            </header>

            <div
              data-testid="insight-body"
              className="prose-emb"
            >
              {renderBody(post.body)}
            </div>

            <div className="mt-16 pt-10 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-6 items-start">
              <div>
                <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-2">
                  ¿Te interesa hablar con un asesor?
                </div>
                <p className="text-sm text-[#F9F6F0]/65 max-w-md">
                  La asesoría inicial es sin costo y sin compromiso.
                </p>
              </div>
              <Link
                to="/#contacto"
                data-testid="insight-cta"
                className="inline-flex items-center gap-2 bg-[#3D1565] hover:bg-[#4a1c79] text-white text-sm tracking-wider uppercase px-7 py-3.5 rounded-sm transition-all duration-300 hover:-translate-y-0.5 shrink-0"
              >
                Agendar diagnóstico
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </>
        )}
      </article>
    </PageShell>
  );
};

export default InsightDetail;
